import { useCallback, useRef, useState } from 'react';
import { useDriverOffers } from './DriverOffersProvider';

const offerKey = (o) => `${o.table}:${o.id}`;

/**
 * Accept / decline for a driver offer, with the busy and message state a
 * surface needs to render them.
 *
 * Extracted from DriverHomePage on 2026-09-03 so the home card and the
 * full-screen offer run the same code. Since the rider's work moved to InGo's
 * API (M5, 30 Sep 2026) both run the provider's `acceptOffer` / `declineOffer`,
 * which the notification buttons run too: one accept path, in one place.
 *
 * There is no counter-offer: the client books at the price InGo quoted, and a
 * rider takes the job at that price or lets it pass.
 *
 * Each action resolves to the underlying result (`{ ok, ... }`); the message
 * and busy state are already set by then.
 */
export function useOfferActions() {
  const { acceptOffer, declineOffer } = useDriverOffers();

  const [actionMsg, setActionMsg] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const acceptingRef = useRef(false);

  const accept = useCallback(
    async (offer) => {
      const k = offerKey(offer);
      if (acceptingRef.current || busyKey) return { ok: false, error: 'Busy.' };
      acceptingRef.current = true;
      setBusyKey(k);
      setActionMsg('');
      try {
        const res = await acceptOffer(offer);
        // Taken by another rider: the provider's own dialog says so.
        if (!res.ok && !res.taken) setActionMsg(res.error || 'Could not accept this request.');
        return res;
      } finally {
        setBusyKey('');
        acceptingRef.current = false;
      }
    },
    [acceptOffer, busyKey],
  );

  const reject = useCallback(
    async (offer) => {
      setBusyKey(offerKey(offer));
      setActionMsg('');
      try {
        const res = await declineOffer(offer);
        if (!res.ok) setActionMsg(res.error || 'Could not save your decline.');
        return res;
      } finally {
        setBusyKey('');
      }
    },
    [declineOffer],
  );

  return {
    accept,
    reject,
    actionMsg,
    setActionMsg,
    busyKey,
  };
}
